import { HeaderGroup, flexRender } from "@tanstack/react-table";
import { ChevronDown, ChevronUp } from "lucide-react";

import { useTheme } from "@/components/theme-provider";
import * as Table from "@/components/ui/table";
import { SELECTOR_ID } from "@/constants/global-constants";
import { ResponsePagination, SortDirection } from "@/hooks/usePagination";
import { cn } from "@/lib/utils";

type TableHeaderProps<T> = {
  headerGroup: HeaderGroup<T>;
  pagination?: ResponsePagination;
};

const SortIcon = ({
  direction,
}: {
  direction?: SortDirection;
}): JSX.Element => {
  if (!direction) {
    return <></>;
  }

  if (direction === "asc") {
    return <ChevronUp className='w-4 h-4 ml-2' />;
  }

  return <ChevronDown className='w-4 h-4 ml-2' />;
};

const TableHeader = <T,>({
  headerGroup,
  pagination,
}: TableHeaderProps<T>): React.JSX.Element => {
  const { theme: skin } = useTheme();

  const handleSort = pagination?.handleSort;
  const isSorted = pagination?.isSorted;
  const getSortDirection = pagination?.getSortDirection;

  return (
    <Table.TableRow key={headerGroup.id}>
      {headerGroup.headers.map((header, headerIdx) => {
        const columnRelativeDepth = header.depth - header.column.depth;

        if (columnRelativeDepth > 1) {
          return null;
        }

        let rowSpan = 1;
        if (header.isPlaceholder) {
          const leafs = header.getLeafHeaders();
          rowSpan = leafs[leafs.length - 1].depth - header.depth;
        }
        const hasParentHeader = header.depth > 1;

        // @ts-expect-error sortBy exists
        const headerSortBy = header.column.columnDef.sortBy;
        const isSelectId = header.column.columnDef.id === SELECTOR_ID;

        return (
          <Table.TableHead
            key={`${header.id}-${headerIdx}`}
            colSpan={header.colSpan}
            style={{
              width:
                header.getSize() === Number.MAX_SAFE_INTEGER
                  ? "auto"
                  : header.getSize(),
              minWidth: header.column.columnDef.meta?.minWidth,
              height: hasParentHeader ? "0px" : "auto",
              paddingBottom: hasParentHeader ? "10px" : "auto",
            }}
            className={cn(
              "text-primary capitalize ",
              skin === "dark" && "text-white",
              skin !== "dark" ? "bg-[#E4E8F2]" : "bg-primaryLight",
              header.column?.columnDef?.meta?.className,
              header.column?.columnDef?.meta?.showRightBorder &&
                "border-r border-r-white ",
              isSelectId && "!max-w-[18px] !pl-2 !mx-0",
            )}
            rowSpan={rowSpan}
            onClick={() =>
              headerSortBy && handleSort && handleSort(headerSortBy)
            }
          >
            <div
              className={cn(
                "flex items-center  !min-w-fit whitespace-nowrap text-sm font-medium",
                header.column.getCanSort() && "cursor-pointer select-none  ",
              )}
            >
              <div
                className='flex items-center h-10 gap-2 px-4 py-2 pl-0 capitalize border-0 ring-0 hover:bg-inherit hover:text-inherit'
                style={{
                  height: hasParentHeader ? "0px" : "auto",
                  ...header.column.columnDef.meta?.headerStyle,
                }}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
                {isSorted && getSortDirection && isSorted(headerSortBy) && (
                  <SortIcon direction={getSortDirection(headerSortBy)} />
                )}
              </div>
            </div>
          </Table.TableHead>
        );
      })}
    </Table.TableRow>
  );
};

export default TableHeader;
